import { useParams, useNavigate } from 'react-router-dom';
import { BasePage } from '@/components/ui/base-page';
import { BreadcrumbNav, BreadcrumbItem } from '@/components/ui/breadcrumb-nav';
import { Calendar, Clock, User, Tag } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useEffect, useState } from 'react';

// This component would use some data fetching utility in a real app
// For now we'll just mock it
const mockArticle = {
    slug: 'introduction-to-digital-funds',
    title: 'Introduction to Digital Funds: The Future of Investment',
    publishDate: '2023-05-15',
    readTime: '8 min read',
    author: {
        name: 'Jane Smith',
        avatar: 'https://randomuser.me/api/portraits/women/44.jpg',
        title: 'Investment Strategist'
    },
    category: 'Investment',
    tags: ['Digital Funds', 'Investment', 'Technology', 'Finance'],
    coverImage: 'https://images.unsplash.com/photo-1526304640581-d334cdbbf45e?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=2070&q=80',
    excerpt: 'Digital funds are changing how we think about investment vehicles, offering unprecedented access and flexibility.',
    content: `
    <p class="lead">Digital funds represent the next evolution in investment products, combining technology with financial innovation to create more accessible, transparent, and efficient investment vehicles.</p>
    
    <h2>The Rise of Digital Investment Products</h2>
    <p>Traditional investment products have been limited by geography, high minimum investments, and complicated access requirements. Digital funds leverage technology to break down these barriers, creating opportunities for a broader range of investors.</p>
    
    <p>By digitizing assets and investment structures, these new vehicles offer:</p>
    <ul>
      <li>Lower minimum investments</li>
      <li>24/7 market access</li>
      <li>Improved transparency</li>
      <li>Reduced intermediary costs</li>
      <li>Faster settlement times</li>
    </ul>
    
    <h2>Key Categories of Digital Funds</h2>
    <p>The digital funds ecosystem encompasses several innovative product categories:</p>
    
    <h3>1. Exchange Traded Products (ETPs)</h3>
    <p>Digital ETPs provide exposure to diverse asset classes through tokenized securities that can be traded on digital exchanges. Unlike traditional ETFs, these products can often be traded outside regular market hours and may have lower expense ratios due to reduced operational costs.</p>
    
    <h3>2. Tokenized Real Assets</h3>
    <p>Real assets like real estate, farmland, and infrastructure can now be tokenized, allowing investors to purchase fractional ownership in previously inaccessible investments. This democratizes access to alternative investments that have historically been reserved for institutional investors.</p>
    
    <p>For example, instead of needing millions to invest in commercial real estate, investors can purchase tokens representing ownership in a property for as little as a few hundred dollars.</p>
    
    <h3>3. Digital Debt Instruments</h3>
    <p>Municipal bonds, corporate debt, and private credit are being reimagined as digital instruments, improving liquidity and access while reducing administrative overhead.</p>
    
    <h2>The Technology Behind Digital Funds</h2>
    <p>Several key technologies enable the digital funds revolution:</p>
    
    <h3>Distributed Ledger Technology</h3>
    <p>Blockchain and other distributed ledger technologies provide the infrastructure for many digital funds, enabling secure, transparent record-keeping without centralized intermediaries.</p>
    
    <h3>Smart Contracts</h3>
    <p>Self-executing contracts with the terms directly written into code allow for automated operations, reducing costs and eliminating the need for certain intermediaries.</p>
    
    <h3>Digital Identity and KYC Solutions</h3>
    <p>Advanced digital identity verification enables compliant investor onboarding while improving the user experience.</p>
    
    <h2>The Future of Digital Funds</h2>
    <p>As regulatory frameworks evolve and technology continues to advance, we expect to see digital funds become increasingly mainstream. Institutional adoption is accelerating, lending legitimacy and bringing more capital into the ecosystem.</p>
    
    <p>Forward-thinking investment firms and financial institutions are already developing digital fund strategies to remain competitive in this rapidly evolving landscape. Those who fail to adapt risk being left behind as investors increasingly demand the benefits that digital funds offer.</p>
    
    <p>In our next article, we'll explore specific digital fund products and how they can be integrated into a diversified investment portfolio.</p>
  `
};

export function ArticlePage() {
    const { slug } = useParams<{ slug: string }>();
    const navigate = useNavigate();
    const [article, setArticle] = useState(mockArticle);
    const [isLoading, setIsLoading] = useState(true);

    // In a real app, you would fetch the article data based on the slug
    useEffect(() => {
        // Mock fetch delay
        const timer = setTimeout(() => {
            setIsLoading(false);
            // If article not found, redirect
            if (slug !== mockArticle.slug) {
                navigate('/insights/blog', { replace: true });
            }
        }, 500);

        return () => clearTimeout(timer);
    }, [slug, navigate]);

    if (isLoading) {
        return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
    }

    const breadcrumbs: BreadcrumbItem[] = [
        { label: "Insights", href: "/insights" },
        { label: "Blog", href: "/insights/blog" },
        { label: article.title, href: `/article/${slug}` }
    ];

    return (
        <BasePage title={article.title} description={article.excerpt}>
            {/* Breadcrumb navigation above the hero section */}
            <div className="container mt-6 mb-4">
                <BreadcrumbNav items={breadcrumbs} />
            </div>

            {/* Hero section with background image */}
            <div
                className="relative py-24 mb-8"
                style={{
                    backgroundImage: `url(${article.coverImage})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center'
                }}
            >
                <div className="absolute inset-0 bg-black/60" />
                <div className="container relative z-10">
                    <div className="max-w-3xl mx-auto text-center">
                        <div className="inline-block bg-primary/90 text-white px-4 py-1 rounded-full text-sm font-medium mb-4">
                            {article.category}
                        </div>

                        <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                            {article.title}
                        </h1>

                        <div className="flex flex-wrap items-center justify-center gap-4 text-white/90 text-sm">
                            <div className="flex items-center">
                                <Calendar className="w-4 h-4 mr-1" />
                                <span>{article.publishDate}</span>
                            </div>

                            <div className="flex items-center">
                                <Clock className="w-4 h-4 mr-1" />
                                <span>{article.readTime}</span>
                            </div>

                            <div className="flex items-center">
                                <User className="w-4 h-4 mr-1" />
                                <span>{article.author.name}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Author info */}
            <div className="container mb-8">
                <div className="max-w-3xl mx-auto">
                    <div className="flex items-center p-4 bg-muted rounded-lg">
                        <div className="w-16 h-16 rounded-full overflow-hidden mr-4">
                            <img
                                src={article.author.avatar}
                                alt={article.author.name}
                                className="w-full h-full object-cover"
                            />
                        </div>
                        <div>
                            <h3 className="font-semibold">{article.author.name}</h3>
                            <p className="text-sm text-muted-foreground">{article.author.title}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Article content */}
            <div className="container pb-16">
                <div className="max-w-3xl mx-auto">
                    <div
                        className="prose prose-lg max-w-none mb-8 prose-headings:font-bold prose-headings:text-primary prose-a:text-primary"
                        dangerouslySetInnerHTML={{ __html: article.content }}
                    />

                    {/* Tags */}
                    <div className="flex flex-wrap gap-2 mb-8 items-center">
                        <Tag className="w-4 h-4 mr-1" />
                        {article.tags.map(tag => (
                            <span key={tag} className="bg-muted px-3 py-1 rounded-full text-sm">
                                {tag}
                            </span>
                        ))}
                    </div>

                    {/* Share and navigation */}
                    <div className="flex justify-between items-center border-t pt-6">
                        <Button variant="outline" onClick={() => navigate('/insights/blog')}>
                            Back to Blog
                        </Button>

                        <div className="flex gap-2">
                            <Button variant="outline" size="icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M5.026 15c6.038 0 9.341-5.003 9.341-9.334 0-.14 0-.282-.006-.422A6.685 6.685 0 0 0 16 3.542a6.658 6.658 0 0 1-1.889.518 3.301 3.301 0 0 0 1.447-1.817 6.533 6.533 0 0 1-2.087.793A3.286 3.286 0 0 0 7.875 6.03a9.325 9.325 0 0 1-6.767-3.429 3.289 3.289 0 0 0 1.018 4.382A3.323 3.323 0 0 1 .64 6.575v.045a3.288 3.288 0 0 0 2.632 3.218 3.203 3.203 0 0 1-.865.115 3.23 3.23 0 0 1-.614-.057 3.283 3.283 0 0 0 3.067 2.277A6.588 6.588 0 0 1 .78 13.58a6.32 6.32 0 0 1-.78-.045A9.344 9.344 0 0 0 5.026 15z" />
                                </svg>
                            </Button>
                            <Button variant="outline" size="icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M16 8.049c0-4.446-3.582-8.05-8-8.05C3.58 0-.002 3.603-.002 8.05c0 4.017 2.926 7.347 6.75 7.951v-5.625h-2.03V8.05H6.75V6.275c0-2.017 1.195-3.131 3.022-3.131.876 0 1.791.157 1.791.157v1.98h-1.009c-.993 0-1.303.621-1.303 1.258v1.51h2.218l-.354 2.326H9.25V16c3.824-.604 6.75-3.934 6.75-7.951z" />
                                </svg>
                            </Button>
                            <Button variant="outline" size="icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                                    <path d="M0 1.146C0 .513.526 0 1.175 0h13.65C15.474 0 16 .513 16 1.146v13.708c0 .633-.526 1.146-1.175 1.146H1.175C.526 16 0 15.487 0 14.854V1.146zm4.943 12.248V6.169H2.542v7.225h2.401zm-1.2-8.212c.837 0 1.358-.554 1.358-1.248-.015-.709-.52-1.248-1.342-1.248-.822 0-1.359.54-1.359 1.248 0 .694.521 1.248 1.327 1.248h.016zm4.908 8.212V9.359c0-.216.016-.432.08-.586.173-.431.568-.878 1.232-.878.869 0 1.216.662 1.216 1.634v3.865h2.401V9.25c0-2.22-1.184-3.252-2.764-3.252-1.274 0-1.845.7-2.165 1.193v.025h-.016a5.54 5.54 0 0 1 .016-.025V6.169h-2.4c.03.678 0 7.225 0 7.225h2.4z" />
                                </svg>
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </BasePage>
    );
} 